/**
 * Host dashboard model — README §3 "Host event operations".
 *
 * Pure helpers the tabbed EventOperationPage and its tab components share. Every
 * number here is counted off the applications list (or the money DTO when the
 * backend has one) so the stats, the tab counts and the guest list can never
 * disagree with each other. No fabricated figures: when a value is unknown it is
 * `null` and the UI prints `—`.
 */
import { money, dateLabel } from '../../utils/surfaceFormat';

/** Application actions (accept / decline / reinvite / remove) are only live in these statuses. */
export const ACTION_STATUSES = ['selling', 'funding', 'greenlit'];

/** Statuses a host may still edit the event in (mirrors the old EDIT EVENT gate). */
export const EDITABLE_STATUSES = ['draft', 'selling', 'funding', 'greenlit'];

/** Statuses a broadcast may go out in (mirrors the old BROADCAST gate). */
export const BROADCAST_STATUSES = ['selling', 'funding', 'greenlit', 'ended'];

/** Statuses the host can cancel from (mirrors the old CANCEL EVENT gate). */
export const CANCELABLE_STATUSES = ['funding', 'selling', 'greenlit', 'processing_greenlight'];

/** Statuses where there are real tickets to scan. */
export const DOOR_STATUSES = ['selling', 'greenlit', 'ended'];

export const STEP_TABS = [
  { key: 1, label: '1. BASICS' },
  { key: 2, label: '2. PLACE' },
  { key: 3, label: '3. TICKETING' },
  { key: 4, label: '4. EXTRAS' },
  { key: 5, label: '5. REVIEW' },
];

export function bucketApplications(applications = []) {
  const by = (s) => applications.filter((a) => a.status === s);
  const pending = by('pending');
  const accepted = by('accepted');
  const paid = by('paid');
  return {
    pending,
    accepted,
    paid,
    declined: by('declined'),
    waitlisted: by('waitlisted'),
    // Applies started but never paid — the 48h sweeper withdraws them; until then the host can nudge.
    pendingPayment: by('pending_payment'),
    // External-ticketing click-throughs (read-only).
    interested: by('external_interest'),
    // Everyone confirmed to come — paid first, as the old ATTENDING grid asserted.
    attending: [...paid, ...accepted],
  };
}

export const ticketCount = (apps = []) => apps.reduce((n, a) => n + (a.quantity || 1), 0);

/** Ticket revenue this application holds in escrow (falls back to the pledge). */
export const committedCents = (app) => (app?.partyCreditsCommitted > 0 ? app.partyCreditsCommitted : (app?.pledgeAmountCents || 0));

/** What the guest actually paid, fees included, when the checkout split is known. */
export const paidCents = (app) => {
  const split = (app?.cardPortionCents || 0) + (app?.balancePortionCents || 0);
  return split > 0 ? split : committedCents(app);
};

export const guestName = (app) => app?.guest?.displayName || app?.guestCheckoutName || 'Unknown';
export const guestEmail = (app) => app?.guestEmail || app?.guestCheckoutEmail || '';

/** How many of this purchase's tickets are through the door. */
export function checkedInCount(app) {
  const tickets = app?.tickets || [];
  if (tickets.length) return tickets.filter((t) => t.attendanceStatus === 'attended').length;
  return app?.attendanceStatus === 'attended' ? (app.quantity || 1) : 0;
}

export const isCheckedIn = (app) => {
  const n = checkedInCount(app);
  return n > 0 && n >= (app?.quantity || 1);
};

/** Funding progress for a Party Starter — the same sum the old page drew its bar from. */
export function fundingProgress(event, applications = []) {
  const current = applications
    .filter((a) => ['accepted', 'pending', 'paid'].includes(a.status))
    .reduce((sum, a) => sum + (a.pledgeAmountCents || committedCents(a)), 0);
  const target = event?.fundingGoalCents || 0;
  return { currentCents: current, targetCents: target, percentage: target > 0 ? (current / target) * 100 : 0 };
}

/**
 * The 5-up header stats. `money` is the GET /events/{id}/money DTO when it exists;
 * without it escrow is summed from the applications.
 */
export function opsStats({ event, applications = [], money: moneyDto = null }) {
  const b = bucketApplications(applications);
  const sold = ticketCount(b.attending);
  const escrowCents = moneyDto && typeof moneyDto.escrowHeldCents === 'number'
    ? moneyDto.escrowHeldCents
    : b.attending.filter((a) => !a.partyCreditsRefunded).reduce((s, a) => s + committedCents(a), 0);
  return {
    sold,
    capacity: event?.maxAttendees || null,
    escrowCents,
    waitlist: b.waitlisted.length,
    toReview: b.pending.length,
    checkedIn: b.paid.filter(isCheckedIn).length,
  };
}

const PRIORITY = { paid: 0, accepted: 1, pending: 2, waitlisted: 3, pending_payment: 4, external_interest: 5, declined: 6 };

/** Guest list order: ticket holders first, then the queue, then the rest — oldest first within a group (the roster reads like a ledger). */
export function sortForGuestList(applications = []) {
  return [...applications].sort((a, b) => {
    const pa = PRIORITY[a.status] ?? 9;
    const pb = PRIORITY[b.status] ?? 9;
    if (pa !== pb) return pa - pb;
    return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
  });
}

const TICKETS = (n) => `${n} ${n === 1 ? 'TICKET' : 'TICKETS'}`;

/**
 * One guest-list row: name, meta line, status pill and the amount column.
 * Pills: CHECKED IN green · PAID black · TO REVIEW yellow · WAITLIST grey · REFUNDED red
 * outline · ACCEPTED outline · PENDING PAYMENT ghost · INTERESTED hairline · DECLINED ghost.
 */
export function guestRow(app, { waitlistPosition } = {}) {
  const qty = app.quantity || 1;
  const when = dateLabel(app.createdAt);
  const base = {
    id: app.id,
    app,
    name: guestName(app),
    email: guestEmail(app),
    userId: app.guest?.userId || null,
    handle: app.guest?.handle || null,
    avatarUrl: app.guest?.avatarUrl || null,
    quantity: qty,
    amountCents: null,
    amountText: '—',
    checkedIn: isCheckedIn(app),
    checkedInCount: checkedInCount(app),
  };
  switch (app.status) {
    case 'paid': {
      if (app.partyCreditsRefunded) {
        const cents = committedCents(app);
        return { ...base, meta: `REFUNDED · ${when}`, pill: { label: 'REFUNDED', tone: 'redOutline' }, amountCents: -cents, amountText: money(-cents) };
      }
      const cents = paidCents(app);
      const inCount = checkedInCount(app);
      const partial = inCount > 0 && inCount < qty ? ` · ${inCount} OF ${qty} IN` : '';
      return {
        ...base,
        meta: `PAID · ${TICKETS(qty)} · ${when}${partial}`,
        pill: isCheckedIn(app) ? { label: 'CHECKED IN', tone: 'green' } : { label: 'PAID', tone: 'black' },
        amountCents: cents,
        amountText: money(cents),
      };
    }
    case 'accepted': {
      if (app.partyCreditsRefunded) {
        const cents = committedCents(app);
        return { ...base, meta: `REFUNDED · ${when}`, pill: { label: 'REFUNDED', tone: 'redOutline' }, amountCents: -cents, amountText: money(-cents) };
      }
      const cents = committedCents(app);
      return { ...base, meta: `ACCEPTED · ${TICKETS(qty)} · ${when}`, pill: { label: 'ACCEPTED', tone: 'outline' }, amountCents: cents, amountText: cents > 0 ? money(cents) : '—' };
    }
    case 'pending': {
      const cents = committedCents(app);
      return { ...base, meta: `APPLIED · ${when}`, pill: { label: 'TO REVIEW', tone: 'yellow' }, amountCents: cents, amountText: cents > 0 ? money(cents) : '—' };
    }
    case 'waitlisted':
      return { ...base, meta: `WAITLIST${waitlistPosition ? ` #${waitlistPosition}` : ''} · ${when}`, pill: { label: 'WAITLIST', tone: 'grey' } };
    case 'pending_payment':
      return { ...base, meta: `APPLIED · ${when} · NOT PAID`, pill: { label: 'PENDING PAYMENT', tone: 'ghost' } };
    case 'external_interest':
      return { ...base, meta: `CLICKED THROUGH · ${when}`, pill: { label: 'INTERESTED', tone: 'hairline' } };
    case 'declined': {
      if (app.partyCreditsRefunded) {
        const cents = committedCents(app);
        return { ...base, meta: `DECLINED · REFUNDED · ${when}`, pill: { label: 'DECLINED', tone: 'ghost' }, amountCents: -cents, amountText: money(-cents) };
      }
      return { ...base, meta: `DECLINED · ${when}`, pill: { label: 'DECLINED', tone: 'ghost' } };
    }
    case 'withdrawn':
      return { ...base, meta: `WITHDREW · ${when}`, pill: { label: 'WITHDRAWN', tone: 'ghost' } };
    case 'payment_failed':
      return { ...base, meta: `PAYMENT FAILED · ${when}`, pill: { label: 'PAYMENT FAILED', tone: 'redOutline' } };
    default:
      return { ...base, meta: `${String(app.status || '').replace(/_/g, ' ').toUpperCase()} · ${when}`, pill: { label: String(app.status || '—').replace(/_/g, ' ').toUpperCase(), tone: 'ghost' } };
  }
}

/** Every application as a guest-list row, in list order, with waitlist positions. */
export function guestRows(applications = []) {
  let wait = 0;
  return sortForGuestList(applications).map((a) => guestRow(a, { waitlistPosition: a.status === 'waitlisted' ? ++wait : undefined }));
}

const csvCell = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

/** EXPORT — the full list as CSV: name, email, status, tickets, paid, applied, checked-in. */
export function buildGuestCsv(applications = []) {
  const header = ['Name', 'Email', 'Status', 'Tickets', 'Paid', 'Applied', 'Checked in'];
  const lines = sortForGuestList(applications).map((app) => {
    const qty = app.quantity || 1;
    const inCount = checkedInCount(app);
    const paid = ['paid', 'accepted'].includes(app.status) && !app.partyCreditsRefunded ? (paidCents(app) / 100).toFixed(2) : '0.00';
    const checked = inCount === 0 ? 'no' : inCount >= qty ? 'yes' : `partial (${inCount}/${qty})`;
    const applied = app.createdAt ? new Date(app.createdAt).toISOString().slice(0, 10) : '';
    return [guestName(app), guestEmail(app), app.status || '', qty, paid, applied, checked].map(csvCell).join(',');
  });
  return [header.join(','), ...lines].join('\r\n');
}

/**
 * MONEY tab fallback while GET /events/{id}/money is still landing: sales and refunds
 * from the applications, fees from the fee rules. Flagged `estimated`; no ledger.
 * Guaranteed: the host is credited the sticker price (PR fee, GST and processing are
 * paid by the guest on top). Party Starter: 7% host fee + 5% GST on the fee at greenlight.
 */
export function fallbackMoney({ event, applications = [], hostFeePercent = 7, gstPercent = 5 }) {
  const b = bucketApplications(applications);
  const live = b.attending.filter((a) => !a.partyCreditsRefunded);
  const refunded = applications.filter((a) => a.partyCreditsRefunded);
  const ticketRevenueCents = live.reduce((s, a) => s + committedCents(a), 0);
  const refundedCents = refunded.reduce((s, a) => s + committedCents(a), 0);
  const starter = event?.fundingType === 'party_starter';
  const hostFeeCents = starter ? Math.round(ticketRevenueCents * (hostFeePercent / 100)) : 0;
  const hostFeeGstCents = starter ? Math.round(hostFeeCents * (gstPercent / 100)) : 0;
  const netToHostCents = ticketRevenueCents - hostFeeCents - hostFeeGstCents;
  const released = event?.escrowReleaseAt ? new Date(event.escrowReleaseAt) <= new Date() : false;
  return {
    estimated: true,
    currency: event?.currencyCode || 'CAD',
    ticketRevenueCents,
    refundedCents,
    escrowHeldCents: released ? 0 : ticketRevenueCents,
    releasedCents: released ? netToHostCents : 0,
    escrowReleaseAt: event?.escrowReleaseAt || null,
    hostFeeCents,
    hostFeeGstCents,
    netToHostCents,
    fundingType: event?.fundingType || 'guaranteed',
    lines: [],
  };
}

/** The 24h-after-doors release moment, for the ESCROW notes. */
export function escrowReleaseMoment(event, moneyDto) {
  const at = moneyDto?.escrowReleaseAt || event?.escrowReleaseAt;
  if (at) return new Date(at);
  const end = event?.endDateTime || event?.dateTime;
  if (!end) return null;
  return new Date(new Date(end).getTime() + 24 * 60 * 60 * 1000);
}
